import { all, AllEffect, ForkEffect } from 'redux-saga/effects';
import { Manager, type Socket } from 'socket.io-client';

import { watch as watchBrand } from './reducers/brand/saga';
import { watch as watchCarrier } from './reducers/carrier/saga';
import { watch as watchCategories } from './reducers/categories/saga';
import { watch as watchCategory } from './reducers/category/saga';
import { watch as watchComparisonPortal } from './reducers/comparison-portal/saga';
import { watch as watchCountries } from './reducers/countries/saga';
import { watch as watchCountry } from './reducers/country/saga';
import { watch as watchCountryLanguages } from './reducers/country-languages/saga';
import { watch as watchCrmCustomerDetail } from './reducers/crm-customer-detail/saga';
import { watch as watchCrmCustomers } from './reducers/crm-customers/saga';
import { watch as watchDashboard } from './reducers/dashboard/saga';
import { watch as watchDelivery } from './reducers/delivery/saga';
import { watch as watchDiscount } from './reducers/discount/saga';
import { watch as watchDownloader } from './reducers/downloader/saga';
import { watch as watchEbay } from './reducers/ebay/saga';
import { watch as watchEmailValidator } from './reducers/email-validator/saga';
import { watch as watchFacebook } from './reducers/facebook/saga';
import { watch as watchFeatureFlags } from './reducers/feature-flags/saga';
import { watch as watchGiftRegistry } from './reducers/gift-registry/saga';
import { watch as watchMarketingConsentCampaign } from './reducers/marketing-consent-campaign/saga';
import { watch as watchMessenger } from './reducers/messenger/saga';
import { watch as watchOrder } from './reducers/order/saga';
import { watch as watchOrders } from './reducers/orders/saga';
import { watch as watchPackets } from './reducers/packets/saga';
import { watch as watchProduct } from './reducers/product/saga';
import { watch as watchProductSafety } from './reducers/product-safety/saga';
import { watch as watchProducts } from './reducers/products/saga';
import { watch as watchProperties } from './reducers/properties/saga';
import { watch as watchProperty } from './reducers/property/saga';
import { watch as watchReport } from './reducers/report/saga';
import { watch as watchRequestFailed } from './reducers/request-failed/saga';
import { watch as watchSearch } from './reducers/search/saga';
import { watch as watchSettings } from './reducers/settings/saga';
import { watch as watchSocialNetworks } from './reducers/social-networks/saga';
import { watch as watchTags } from './reducers/tags/saga';
import { watch as watchUser } from './reducers/user/saga';
import { watch as watchWarehouseOrder } from './reducers/warehouse-order/saga';
import { watch as watchWatchdog } from './reducers/watchdog/saga';
import { watch as watchWebAnalyticsInsights } from './reducers/web-analytics-insights/saga';
import { watch as watchWithdrawal } from './reducers/withdrawal/saga';

// Lazy initialization to ensure window is available and prevent issues with webpack module loading
let socket: Socket | null = null;

const createSocket = (): Socket => {
  if (!socket) {
    // Ensure we have access to window and a valid origin
    if (typeof window === 'undefined') {
      throw new Error(
        'Socket.IO can only be initialized in browser environment',
      );
    }

    const origin: string = window.location.origin;
    if (!origin) {
      throw new Error(
        'Unable to determine window origin for socket connection',
      );
    }

    const manager = new Manager(origin, {
      // Add connection options for better reliability
      autoConnect: true,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
      timeout: 20000,
    });

    socket = manager.socket('/');
  }

  return socket;
};

export const getSocket = (): Socket => createSocket();

const watch = function* (): Generator<ForkEffect<never>, void, string> {
  // getSocket() called lazily here — only when redux-saga advances this generator
  const socketInstance = getSocket();

  yield* watchBrand(socketInstance);
  yield* watchCarrier(socketInstance);
  yield* watchCategories(socketInstance);
  yield* watchCategory(socketInstance);
  yield* watchComparisonPortal(socketInstance);
  yield* watchCountries(socketInstance);
  yield* watchCountry(socketInstance);
  yield* watchCountryLanguages(socketInstance);
  yield* watchDashboard(socketInstance);
  yield* watchDelivery(socketInstance);
  yield* watchDiscount(socketInstance);
  yield* watchDownloader(socketInstance);
  yield* watchEbay(socketInstance);
  yield* watchEmailValidator(socketInstance);
  yield* watchFacebook(socketInstance);
  yield* watchFeatureFlags(socketInstance);
  yield* watchGiftRegistry(socketInstance);
  yield* watchMarketingConsentCampaign(socketInstance);
  yield* watchMessenger(socketInstance);
  yield* watchOrder(socketInstance);
  yield* watchOrders(socketInstance);
  yield* watchPackets(socketInstance);
  yield* watchProduct(socketInstance);
  yield* watchProductSafety(socketInstance);
  yield* watchProducts(socketInstance);
  yield* watchProperties(socketInstance);
  yield* watchProperty(socketInstance);
  yield* watchReport(socketInstance);
  yield* watchRequestFailed();
  yield* watchSearch(socketInstance);
  yield* watchSettings(socketInstance);
  yield* watchSocialNetworks(socketInstance);
  yield* watchTags(socketInstance);
  yield* watchUser(socketInstance);
  yield* watchWarehouseOrder(socketInstance);
  yield* watchWatchdog(socketInstance);
  yield* watchWebAnalyticsInsights(socketInstance);
  yield* watchWithdrawal(socketInstance);
  yield* watchCrmCustomers(socketInstance);
  yield* watchCrmCustomerDetail(socketInstance);
};

export const mySaga = function* (): Generator<
  AllEffect<Generator<ForkEffect<never>, void, string>>,
  void,
  void
> {
  yield all([watch()]);
};
